// One settings field as a control: the widget follows the descriptor's type and enum, every label
// goes through <Field help=…>, and the state (inherited / pinned by deployment / customer-readonly)
// shows beside it. A list of strings is edited one entry per row (the list editor), each entry
// checked against the schema's item rule; a list or object with no other widget is edited as JSON.
// Labels, hints and enum options read through the display map (labels.ts), never raw schema text.
import { useState } from "preact/hooks";
import { Checkbox, errorId, Field } from "../primitives/field";
import type { HelpId } from "../primitives/help";
import { ListEditor } from "../primitives/list-editor";
import { Chip } from "../primitives/page";
import type { Json } from "./inherit";
import { fieldHint, fieldLabel, itemExample, itemFormat, optionLabel } from "./labels";
import type { FieldMode } from "./pins";
import { isStringList, itemProblem, type FieldDescriptor } from "./validate";

export interface FieldRendererProps {
  field: FieldDescriptor;
  /** The value shown: the override's, else the inherited one. */
  value: Json | undefined;
  mode: FieldMode;
  inherited: boolean;
  error?: string | undefined;
  onChange(value: Json | undefined): void;
}

export function controlId(path: string): string {
  return `set-${path.replace(/\./g, "-")}`;
}

function JsonBox({ id, value, disabled, invalid, onChange }: { id: string; value: Json | undefined; disabled: boolean; invalid: boolean; onChange(value: Json | undefined): void }) {
  const [text, setText] = useState(value === undefined ? "" : JSON.stringify(value, null, 2));
  const [broken, setBroken] = useState(false);
  return (
    <textarea
      id={id}
      rows={4}
      value={text}
      disabled={disabled}
      aria-invalid={invalid || broken}
      aria-describedby={invalid ? errorId(id) : undefined}
      onInput={(event) => {
        const next = event.currentTarget.value;
        setText(next);
        try {
          onChange(next.trim() === "" ? undefined : (JSON.parse(next) as Json));
          setBroken(false);
        } catch {
          setBroken(true);
        }
      }}
    />
  );
}

export function FieldRenderer({ field, value, mode, inherited, error, onChange }: FieldRendererProps) {
  const id = controlId(field.path);
  const disabled = mode.kind === "readonly";
  const help = field.help as HelpId;
  const label = fieldLabel(field);
  const notes = (
    <>
      {inherited && <Chip tone="grey">inherited</Chip>}
      {mode.kind === "readonly" && (
        <Chip tone={mode.reason === "pinned" ? "amber" : "blue"} title={mode.note}>
          {mode.reason === "pinned" ? "pinned by deployment" : mode.reason === "customer-readonly" ? "read-only" : "locked"}
        </Chip>
      )}
      {field.apply === "redeploy" && <Chip tone="amber">needs redeploy</Chip>}
    </>
  );
  const hint = (
    <>
      {mode.kind === "readonly" ? `${mode.note}. ` : ""}
      {fieldHint(field)}
      {notes}
    </>
  );

  if (field.type.includes("boolean") && field.type.length === 1) {
    return (
      <div class={inherited ? "settings-field settings-inherited" : "settings-field"}>
        <Checkbox id={id} label={label} help={help} checked={value === true} disabled={disabled} describedBy={error ? errorId(id) : undefined} onChange={(checked) => onChange(checked)} />
        <span class="field-hint">{notes}</span>
        {error ? (
          <p class="field-error" id={errorId(id)} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  if (isStringList(field)) {
    const values = Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
    return (
      <ListEditor
        id={id}
        label={label}
        help={help}
        hint={hint}
        values={values}
        error={error}
        disabled={disabled}
        max={field.max_items}
        placeholder={itemExample(field.path)}
        class={inherited ? "settings-inherited" : undefined}
        check={(entry) => {
          const problem = itemProblem(field, entry);
          return problem && (itemFormat(field.path) ?? problem);
        }}
        onChange={onChange}
      />
    );
  }

  let control;
  if (field.enum && field.type.length === 1) {
    control = (
      <select id={id} value={String(value ?? "")} disabled={disabled} aria-describedby={error ? errorId(id) : undefined} onChange={(event) => onChange(event.currentTarget.value)}>
        {field.enum.map((option) => (
          <option key={String(option)} value={String(option)}>
            {optionLabel(field.path, option)}
          </option>
        ))}
      </select>
    );
  } else if (field.type.length === 1 && (field.type[0] === "integer" || field.type[0] === "number")) {
    control = (
      <input
        id={id}
        type="number"
        value={value === undefined || value === null ? "" : String(value)}
        min={field.minimum ?? undefined}
        max={field.maximum ?? undefined}
        disabled={disabled}
        aria-describedby={error ? errorId(id) : undefined}
        onInput={(event) => onChange(event.currentTarget.value === "" ? undefined : Number(event.currentTarget.value))}
      />
    );
  } else if (field.type.includes("string") && field.type.every((t) => t === "string" || t === "null")) {
    control = (
      <input
        id={id}
        type="text"
        value={typeof value === "string" ? value : ""}
        maxLength={field.max_length ?? undefined}
        disabled={disabled}
        aria-describedby={error ? errorId(id) : undefined}
        onInput={(event) => onChange(event.currentTarget.value === "" && field.type.includes("null") ? null : event.currentTarget.value)}
      />
    );
  } else {
    control = <JsonBox id={id} value={value} disabled={disabled} invalid={Boolean(error)} onChange={onChange} />;
  }
  return (
    <Field id={id} label={label} help={help} hint={hint} error={error} class={inherited ? "settings-inherited" : undefined}>
      {control}
    </Field>
  );
}
